import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

const DESIGNATION_META = {
  'Dynasty Champion':     { icon: '👑', color: 'var(--lor-accent)', desc: 'Won 2+ championships' },
  'Back-to-Back Champion':{ icon: '🔁', color: '#fb923c', desc: 'Consecutive championships' },
  'Cinderella':           { icon: '🌠', color: '#60a5fa', desc: 'Won with a losing reg. season record' },
  'Dominant':             { icon: '💪', color: '#6fbf73', desc: 'Won with the best regular season record' },
};

function DesignationBadge({ label }) {
  const meta = DESIGNATION_META[label];
  if (!meta) return null;
  return (
    <span
      className="lor-hoc-designation"
      title={meta.desc}
      style={{ color: meta.color, borderColor: `${meta.color}44`, background: `${meta.color}12` }}
    >
      {meta.icon} {label}
    </span>
  );
}

function TrophyCard({ entry, isFirst }) {
  const {
    season, champion, runner_up, championship_score,
    champion_record, regular_season_champion, designations,
    narrative, source, total_championships,
  } = entry;

  const isPending = !champion;

  return (
    <div className={`lor-hoc-card lor-card${isFirst ? ' lor-hoc-card--latest' : ''}${isPending ? ' lor-history-season-card--pending' : ''}`}>
      <div className="lor-hoc-year-col">
        <div className="lor-hoc-year">{season}</div>
        {!isPending && (
          <div className="lor-hoc-trophy-icon">
            {total_championships >= 2 ? <Icon emoji="👑" size={20} /> : <Icon emoji="🏆" size={20} />}
          </div>
        )}
      </div>
      <div className="lor-hoc-content">
        {isPending ? (
          <div className="lor-history-pending">Data pending — commissioner entry required</div>
        ) : (
          <>
            <div className="lor-hoc-champion-row">
              <span className="lor-hoc-champion-name">{champion}</span>
              {champion_record && (
                <span className="lor-hoc-record">{champion_record}</span>
              )}
            </div>

            {designations && designations.length > 0 && (
              <div className="lor-hoc-designations">
                {designations.map(d => <DesignationBadge key={d} label={d} />)}
              </div>
            )}

            <div className="lor-hoc-detail-row">
              {runner_up && (
                <span className="lor-hoc-detail">
                  <span style={{ color: '#666' }}>Runner-up:</span> {runner_up}
                </span>
              )}
              {championship_score && (
                <span className="lor-hoc-detail">
                  <span style={{ color: '#666' }}>Final score:</span> {championship_score}
                </span>
              )}
              {regular_season_champion && regular_season_champion !== champion && (
                <span className="lor-hoc-detail">
                  <span style={{ color: '#666' }}>Reg. season leader:</span> {regular_season_champion}
                </span>
              )}
            </div>

            {narrative && (
              <div className="lor-history-narrative" style={{ marginTop: 8 }}>
                "{narrative}"
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function LORHallOfChampions({ api }) {
  const [hall, setHall]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    fetch(`${api}/api/lor/hall-of-champions`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => { setHall(data || []); setLoading(false); })
      .catch(() => { setError('Hall of Champions unavailable.'); setLoading(false); });
  }, [api]);

  if (loading) return <div className="lor-loading">Loading Hall of Champions…</div>;
  if (error)   return <div className="lor-empty">{error}</div>;

  // Championship count per manager
  const champCounts = {};
  for (const entry of hall) {
    if (entry.champion) {
      champCounts[entry.champion] = (champCounts[entry.champion] || 0) + 1;
    }
  }
  const dynasties = Object.entries(champCounts)
    .filter(([, c]) => c >= 2)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="🏆" size={18} /> Hall of Champions</h2>
        <div className="lor-page-meta">League of Record · Est. 2012 · Championship Archive</div>
      </div>

      {dynasties.length > 0 && (
        <div className="lor-card lor-hoc-dynasty-banner">
          <div className="lor-hoc-dynasty-title"><Icon emoji="👑" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Dynasty Champions</div>
          <div className="lor-hoc-dynasty-list">
            {dynasties.map(([name, count]) => (
              <div key={name} className="lor-hoc-dynasty-entry">
                <span className="lor-gold" style={{ fontWeight: 700 }}>{name}</span>
                <span className="lor-champ-badge" style={{ marginLeft: 8 }}><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="lor-hoc-legend">
        {Object.entries(DESIGNATION_META).map(([label, meta]) => (
          <span key={label} className="lor-hoc-legend-item" style={{ color: meta.color }}>
            {meta.icon} {label}
          </span>
        ))}
      </div>

      <div className="lor-hoc-timeline">
        {hall.length === 0 && (
          <div className="lor-empty">No championship data available yet.</div>
        )}
        {hall.map((entry, i) => (
          <TrophyCard key={entry.season} entry={entry} isFirst={i === 0} />
        ))}

        {/* Founding era placeholder */}
        <div className="lor-history-era-header" style={{ marginTop: 24 }}>
          <span>📁 Founding Era (2012–2015)</span>
          <span className="lor-history-era-note">Championship data to be added by commissioner</span>
        </div>
        {[2015, 2014, 2013, 2012].map(yr => (
          <TrophyCard
            key={yr}
            entry={{ season: yr, champion: '', source: 'pending' }}
            isFirst={false}
          />
        ))}
      </div>
    </div>
  );
}
